"use client";

import { ArrowRight } from "lucide-react";
import { RuntimeImage, RuntimeLink } from "../../runtime/RendererRuntime";
import { resolveImageValue } from "../../utils/imageOptions";

/**
 * Category navigation cards — the COMMERCE pattern (autonorma / mister-turbo):
 * compact horizontal cards, square photo thumb left, name + line + reference
 * count right, arrow affordance. Dense and functional — a catalog doorway,
 * not an editorial gallery. 1-col mobile, 3-up from md.
 * Content: { title, subtitle, cards: [{ title, description, image, count, href:{slug} }] }.
 */
type Card = { title?: string; description?: string; image?: unknown; count?: string; href?: { slug?: string } };

export function SpecialtyCardsRenderer({ component }: { component: { content?: { title?: string; subtitle?: string; cards?: Card[] } } }) {
  const { title, subtitle, cards = [] } = component.content ?? {};
  return (
    <section className="py-8 md:py-10">
      {title ? (
        <div className="mb-5">
          <h2 className="text-xl font-bold tracking-tight text-[#0A0A0A] md:text-2xl" style={{ fontFamily: "var(--font-aleo)" }}>{title}</h2>
          {subtitle ? <p className="mt-1 text-sm text-[#6B7280]">{subtitle}</p> : null}
          <div className="mt-2 h-1 w-10 rounded-full bg-[#00B862]" aria-hidden="true" />
        </div>
      ) : null}
      <div className="grid gap-3 md:grid-cols-3 md:gap-4">
        {cards.map((c, i) => {
          const { src } = resolveImageValue(c.image as never);
          return (
            <RuntimeLink
              key={c.title ?? i}
              href={c.href ?? { slug: "" }}
              className="apa-card apa-card-hover group flex items-center gap-4 p-3 md:p-4"
            >
              <div className="h-20 w-20 shrink-0 overflow-hidden rounded-lg bg-[var(--color-surface-2)] md:h-24 md:w-24">
                {src ? (
                  <RuntimeImage
                    src={src}
                    alt={c.title ?? ""}
                    className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                  />
                ) : null}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-bold text-[#0A0A0A] md:text-lg">
                  <span className="apa-underline-reveal">{c.title}</span>
                </h3>
                {c.description ? <p className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-[#6B7280] md:text-sm">{c.description}</p> : null}
                {c.count ? <p className="mt-1 text-xs font-bold text-[#00B862]">{c.count}</p> : null}
              </div>
              <ArrowRight className="h-4 w-4 shrink-0 text-[#9CA3AF] transition group-hover:translate-x-0.5 group-hover:text-[#00B862]" />
            </RuntimeLink>
          );
        })}
      </div>
    </section>
  );
}
